import { getJWTToken, getNetwork, getSpotId, getToken, setJWTToken, setToken } from '../../services/app-state';
import { get, isNil, merge, omitBy } from 'lodash-es';
import config from '@spotim/fed-resources/config';
import authInterceptor from '../interceptors/auth-interceptor';
import mfaInterceptor from '../interceptors/mfa-interceptor';

type HTTPMethod = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH';

const X_SPOTIM_TOKEN = 'x-spotim-token';
const X_SPOTIM_SPOTID = 'x-spotim-spotid';
const X_SPOTIM_NETWORKID = 'x-spotim-networkid';
const X_SPOT_ID = 'x-spot-id';
const X_ACCESS_TOKEN = 'x-access-token';
const X_AUTH_VERSION = 'x-auth-version';
const X_POST_ID = 'x-post-id';
const X_RESET_TOKEN = 'x-reset-token';

type Params = {
  method?: HTTPMethod;
  data?: any;
  service?: string;
  headers?: Partial<THeaders>;
  [key: string]: any;
};

type QueryParams = { [key: string]: string };

type THeaders = {
  [X_SPOT_ID]: string;
  [X_ACCESS_TOKEN]: string;
  [X_AUTH_VERSION]: string;
  [X_SPOTIM_TOKEN]?: string;
  [X_SPOTIM_SPOTID]?: string;
  [X_SPOTIM_NETWORKID]?: string;
  [X_POST_ID]?: string;
};

const pathKey = process.env.SKIP_CDN ? 'url' : 'cdn';

const defaultOptions = {
  method: 'GET',
  mode: 'cors',
  credentials: 'include'
};

const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
};

function getBaseURL(service: string): string {
  return get(config, [service, pathKey]) || config.apiGw[pathKey];
}

function getHeaders({
  service,
  overrideHeaders
}: {
  service: string;
  overrideHeaders?: Partial<THeaders>;
}): Partial<THeaders> {
  let v1Headers = {};

  if (service) {
    v1Headers = {
      [X_SPOTIM_TOKEN]: getToken(),
      [X_SPOTIM_SPOTID]: getSpotId(),
      [X_SPOTIM_NETWORKID]: getNetwork().id
    };
  }

  return omitBy(
    {
      [X_SPOT_ID]: getSpotId(),
      [X_ACCESS_TOKEN]: getJWTToken(),
      [X_SPOTIM_NETWORKID]: getNetwork().id,
      ...v1Headers,
      ...overrideHeaders,
      ...defaultHeaders
    },
    isNil
  );
}

function extendUrlWithQueryParams(url: string, queryParams: QueryParams): string {
  return (
    url +
    '?' +
    Object.keys(queryParams)
      .filter(param => !isNil(queryParams[param]))
      .map(param => `${param}=${queryParams[param]}`)
      .join('&')
  );
}

function setResponseTokenHeaders(
  response: Response,
  { persistence = false }: { persistence: boolean }
) {
  const spotimToken = response.headers.get(X_SPOTIM_TOKEN);
  const accessToken = response.headers.get(X_ACCESS_TOKEN);

  if (spotimToken) {
    setToken(spotimToken);
  }

  if (accessToken) {
    setJWTToken(accessToken);

    if (persistence) {
      window.localStorage?.setItem('SPOTIM_ACCESS_TOKEN', accessToken);
    }
  }
}
function request(url: string, params: Params) {
  const {
    data,
    method = 'GET',
    service,
    headers: overrideHeaders,
    isFetchingFile = false,
    shouldSetResponseTokenHeaders = true,
    shouldRetry = true,
    retries = 0,
    ...restParams
  } = params;
  const headers: Partial<THeaders> = getHeaders({ service, overrideHeaders });
  let fullUrl = getBaseURL(service) + url;

  const requestObject = merge({}, defaultOptions, restParams, { headers, method });

  if (method !== 'GET') {
    Object.assign(requestObject, {
      body: JSON.stringify(data)
    });
  } else if (data) {
    fullUrl = extendUrlWithQueryParams(fullUrl, data);
  }

  const onError = (error: Error) => {
    if (!retries) {
      console.error('[HOST-PANEL REQUEST][ERROR]:', error);
      throw error;
    }

    return request(url, { ...params, retries: retries - 1 });
  };

  return fetch(`${fullUrl}`, requestObject as Request)
    .then(function responseHandling(response: Response) {
      // 202 Accepted
      // 204 No Content

      if (response.headers.get('x-reset-token')) {
        window.localStorage?.removeItem('SPOTIM_ACCESS_TOKEN');
      }

      if (response.status === 202 || response.status === 204) {
        return;
      }

      if ((response.status === 401 || response.status === 403) && shouldRetry) {
        // Initiate logout!
        console.error('[HOST-PANEL REQUEST]: Got 403 status code');
        return authInterceptor();
      }

      if (shouldSetResponseTokenHeaders) {
        setResponseTokenHeaders(response, {
          persistence: restParams?.addJWTtoLocalStorage ?? false
        });
      }

      const responseParsed = isFetchingFile ? response.blob() : response.json();

      return responseParsed.then(data => {
        if (isMFAErrorResponse(data)) {
          mfaInterceptor({ type: data.mfa_error, spotId: getSpotId() });
          return Promise.reject(`[HOST-PANEL REQUEST][ERROR]: MFA V1 returns ${data.mfa_error}`);
        }

        if (response.ok) {
          return data;
        } else {
          return Promise.reject({ status: response.status, data });
        }
      });
    })
    .catch(onError);
}

export default request;

function isMFAErrorResponse(resBody: any): boolean {
  return resBody.success === false && !!resBody.mfa_error;
}
